import { useState } from 'react';
import { api } from '../lib/api';

interface ContentItem {
  id: string;
  userId: string;
  theme: string;
  imageUrl: string;
  blobPath: string;
  blobContainer: string;
  isActive: boolean;
  isServed: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  placement?: string;
  rank_enabled?: boolean;
  rank_number?: number;
  status?: 'draft' | 'ready' | 'rejected' | 'served';
  contentType?: 'reward_card' | 'leaderboard' | 'festival';
  priority?: number;
  generationMetadata?: any;
}

interface DetailPanelProps {
  content: ContentItem;
  onClose: () => void;
  onUpdate: () => void;
}

export function DetailPanel({ content, onClose, onUpdate }: DetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'preview' | 'metadata' | 'json'>('preview');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [status, setStatus] = useState(content.status || 'draft');
  const [placement, setPlacement] = useState(content.placement || 'feed');
  const [priority, setPriority] = useState(content.priority || 20);
  const [contentType, setContentType] = useState(content.contentType || 'reward_card');
  const [isActive, setIsActive] = useState(content.isActive);
  const [isServed, setIsServed] = useState(content.isServed);
  const [rankEnabled, setRankEnabled] = useState(content.rank_enabled || false);
  const [rankNumber, setRankNumber] = useState(content.rank_number || 1);
  const [jsonValue, setJsonValue] = useState(JSON.stringify(content, null, 2));

  const handleSave = async () => {
    try {
      setSaving(true);

      const updates: any = {
        userId: content.userId,
        status,
        placement,
        priority,
        contentType,
        isActive,
        isServed,
        rank_enabled: rankEnabled,
      };

      if (rankEnabled) {
        updates.rank_number = rankNumber;
      }

      await api.updateContent(content.id, updates);
      setEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveJson = async () => {
    try {
      setSaving(true);
      const parsed = JSON.parse(jsonValue);
      
      // Extract only the fields we're allowed to update
      const updates: any = {
        userId: content.userId,
      };

      if (parsed.status) updates.status = parsed.status;
      if (parsed.placement) updates.placement = parsed.placement;
      if (parsed.priority !== undefined) updates.priority = parsed.priority;
      if (parsed.contentType) updates.contentType = parsed.contentType;
      if (parsed.isActive !== undefined) updates.isActive = parsed.isActive;
      if (parsed.isServed !== undefined) updates.isServed = parsed.isServed;
      if (parsed.rank_enabled !== undefined) updates.rank_enabled = parsed.rank_enabled;
      if (parsed.rank_number !== undefined) updates.rank_number = parsed.rank_number;

      await api.updateContent(content.id, updates);
      setEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error('Failed to save JSON:', error);
      alert(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    // Reset form
    setStatus(content.status || 'draft');
    setPlacement(content.placement || 'feed');
    setPriority(content.priority || 20);
    setContentType(content.contentType || 'reward_card');
    setIsActive(content.isActive);
    setIsServed(content.isServed);
    setRankEnabled(content.rank_enabled || false);
    setRankNumber(content.rank_number || 1);
    setJsonValue(JSON.stringify(content, null, 2));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Content Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'preview'
                  ? 'border-linepe-orange text-linepe-orange'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Preview
            </button>
            <button
              onClick={() => setActiveTab('metadata')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'metadata'
                  ? 'border-linepe-orange text-linepe-orange'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Metadata
            </button>
            <button
              onClick={() => setActiveTab('json')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'json'
                  ? 'border-linepe-orange text-linepe-orange'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Raw JSON
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Preview Tab */}
          {activeTab === 'preview' && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="max-w-md">
                  <img
                    src={content.imageUrl}
                    alt={content.theme}
                    className="w-full rounded-lg shadow-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">User ID</p>
                  <p className="font-medium">{content.userId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Theme</p>
                  <p className="font-medium">{content.theme}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="font-medium">{formatDate(content.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Updated</p>
                  <p className="font-medium">{formatDate(content.updatedAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Created By</p>
                  <p className="font-medium">{content.createdBy}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">ID</p>
                  <p className="font-medium text-xs">{content.id}</p>
                </div>
              </div>

              {content.generationMetadata && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Generation Metadata</h3>
                  <div className="space-y-2 text-sm">
                    {content.generationMetadata.modelUsed && (
                      <div>
                        <span className="text-gray-600">Model:</span>{' '}
                        <span className="font-medium">{content.generationMetadata.modelUsed}</span>
                      </div>
                    )}
                    {content.generationMetadata.textGeneration && (
                      <div>
                        <span className="text-gray-600">Text Model:</span>{' '}
                        <span className="font-medium">{content.generationMetadata.textGeneration.model}</span>
                      </div>
                    )}
                    {content.generationMetadata.watermarkApplied !== undefined && (
                      <div>
                        <span className="text-gray-600">Watermark:</span>{' '}
                        <span className="font-medium">{content.generationMetadata.watermarkApplied ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {content.generationMetadata.generationTimeMs && (
                      <div>
                        <span className="text-gray-600">Generation Time:</span>{' '}
                        <span className="font-medium">{(content.generationMetadata.generationTimeMs / 1000).toFixed(2)}s</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Metadata Tab */}
          {activeTab === 'metadata' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ Changes to metadata fields only. Generation prompts cannot be modified.
                </p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as any)}
                      disabled={!editing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange disabled:bg-gray-100"
                    >
                      <option value="draft">Draft</option>
                      <option value="ready">Ready</option>
                      <option value="rejected">Rejected</option>
                      <option value="served">Served</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Content Type
                    </label>
                    <select
                      value={contentType}
                      onChange={(e) => setContentType(e.target.value as any)}
                      disabled={!editing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange disabled:bg-gray-100"
                    >
                      <option value="reward_card">Reward Card</option>
                      <option value="leaderboard">Leaderboard</option>
                      <option value="festival">Festival</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placement
                    </label>
                    <select
                      value={placement}
                      onChange={(e) => setPlacement(e.target.value)}
                      disabled={!editing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange disabled:bg-gray-100"
                    >
                      <option value="feed">Feed</option>
                      <option value="banner">Banner</option>
                      <option value="profile">Profile</option>
                      <option value="featured">Featured</option>
                      <option value="gallery">Gallery</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <input
                      type="number"
                      value={priority}
                      onChange={(e) => setPriority(parseInt(e.target.value))}
                      disabled={!editing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange disabled:bg-gray-100"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={!editing}
                      className="rounded border-gray-300 text-linepe-orange focus:ring-linepe-orange disabled:opacity-50"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Is Active
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isServed}
                      onChange={(e) => setIsServed(e.target.checked)}
                      disabled={!editing}
                      className="rounded border-gray-300 text-linepe-orange focus:ring-linepe-orange disabled:opacity-50"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Is Served
                    </label>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-semibold mb-3">Leaderboard Settings</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={rankEnabled}
                        onChange={(e) => setRankEnabled(e.target.checked)}
                        disabled={!editing}
                        className="rounded border-gray-300 text-linepe-orange focus:ring-linepe-orange disabled:opacity-50"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Rank Enabled
                      </label>
                    </div>

                    {rankEnabled && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rank Number
                        </label>
                        <input
                          type="number"
                          value={rankNumber}
                          onChange={(e) => setRankNumber(parseInt(e.target.value))}
                          disabled={!editing}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange disabled:bg-gray-100"
                          min="1"
                          max="1000000"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Raw JSON Tab */}
          {activeTab === 'json' && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ Advanced users only. Only metadata fields will be updated.
                </p>
              </div>

              <textarea
                value={jsonValue}
                onChange={(e) => setJsonValue(e.target.value)}
                disabled={!editing}
                className="w-full h-[500px] px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange font-mono text-sm disabled:bg-gray-100"
                spellCheck={false}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            {editing && (
              <p className="text-sm text-gray-600">
                Make your changes and click Save
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {!editing ? (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Edit
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={activeTab === 'json' ? handleSaveJson : handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    '💾 Save Changes'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

